import { EdgeProfile, PartItem } from '@cutr/constants/cutlist';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { useActiveGroup } from '@/api/materialsGroup';
import { useActions } from '@/api/store';
import { Modal } from '@/blocks/Modal';
import { Button } from '@/primitives/Button';
import { Cogwheel, Icon } from '@/primitives/Icons';

import { EdgeSelectorProvider, useEdgeSelector } from './EdgeProcessingContext';
import { EdgesSelector } from './EdgeSelector';
import { useSelectedPartsContext } from './SelectedPartsContext';

export const EditEdgeProfiling = () => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = React.useState(false);
  const { selectedParts } = useSelectedPartsContext();

  const tooltip =
    selectedParts.length === 0 ? t('agent.atLeastOnePartTooltip') : undefined;

  const afterApply = () => {
    setIsOpen(false);
    toast.success(t('agent.toasts.edgeProfilingApplied') as string);
  };

  return (
    <>
      <Button
        name="edit-edgeprofiling"
        disabled={selectedParts.length === 0}
        tooltip={tooltip}
        onClick={() => setIsOpen(true)}
        variant="secondary"
        icon={<Icon icon={<Cogwheel />} />}
      >
        {t('agent.editEdgeProfiling')}
      </Button>
      <Modal
        title={t('agent.editEdgeProfiling')}
        handleClose={() => setIsOpen(false)}
        isOpen={isOpen}
      >
        <ModalContent afterApply={afterApply} />
      </Modal>
    </>
  );
};

const parseEdges = (selectedParts: PartItem[]) => {
  const getSideState = (side: keyof EdgeProfile) => {
    const isEnabled = (p: PartItem) =>
      Boolean(p.edgeProfile?.[side]) && p.edgeProfile![side] !== 'none';

    if (selectedParts.every(isEnabled)) return 'on' as const;
    if (selectedParts.some(isEnabled)) return 'mixed' as const;
    return 'off' as const;
  };

  return {
    length1: getSideState('length1'),
    length2: getSideState('length2'),
    width1: getSideState('width1'),
    width2: getSideState('width2'),
  };
};

const ModalContent = ({ afterApply }: { afterApply: () => void }) => {
  const { selectedParts } = useSelectedPartsContext();

  const initialEdges = React.useMemo(
    () => parseEdges(selectedParts),
    [selectedParts]
  );

  return (
    <EdgeSelectorProvider initialEdgesToApply={initialEdges}>
      <ModalForm afterApply={afterApply} />
    </EdgeSelectorProvider>
  );
};

const ModalForm = ({ afterApply }: { afterApply: () => void }) => {
  const { t } = useTranslation();
  const { setPart } = useActions();
  const { selectedParts } = useSelectedPartsContext();
  const activeGroup = useActiveGroup();
  const { edgesToApply } = useEdgeSelector();

  const handleApply = (e: React.FormEvent) => {
    e.preventDefault();

    const groupProfileType = activeGroup?.edgeProfile ?? 'none';

    selectedParts.forEach((part) => {
      const newEdgeProfile: EdgeProfile = {
        length1: edgesToApply.length1 === 'on' ? groupProfileType : 'none',
        length2: edgesToApply.length2 === 'on' ? groupProfileType : 'none',
        width1: edgesToApply.width1 === 'on' ? groupProfileType : 'none',
        width2: edgesToApply.width2 === 'on' ? groupProfileType : 'none',
      };

      setPart({ ...part, edgeProfile: newEdgeProfile });
    });

    afterApply();
  };

  if (!activeGroup) return null;

  return (
    <div className="stack">
      <p>{t(`cutlist-form.edgeProfiling.${activeGroup.edgeProfile}`)}</p>
      <h4>{t('agent.preview.toApplyTo')}</h4>
      <EdgesSelector>
        <strong>{t('agent.preview.willApplyTo')}</strong>
        <br />
        {t('agent.preview.selectedPartsNr', { count: selectedParts.length })}
      </EdgesSelector>
      <Button onClick={handleApply}>{t('agent.applyEdgeProcessing')}</Button>
    </div>
  );
};
